// Yoga Wiltshire - County Hub Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateLocationSchema, SchemaScript } from "../components/shared/SEOHelper";

const YogaWiltshire = () => {
  usePageMeta({
    title: "Yoga Wiltshire | Yoga Teacher in Wiltshire | Annelie Lily",
    description: "Yoga classes for Wiltshire. Small group Hatha & Yin yoga, private sessions based in Frome. Serving Trowbridge, Warminster, Westbury & West Wiltshire",
    canonical: "https://annelielily.com/areas/yoga-wiltshire"
  });

  const schema = generateLocationSchema({
    townName: "Wiltshire",
    description: "Yoga classes for West Wiltshire at Annelie Lily Yoga in Frome. Serving Trowbridge, Warminster, Westbury and surrounding areas.",
    url: "https://annelielily.com/areas/yoga-wiltshire"
  });

  const wiltshireTowns = [
    { name: "Trowbridge", link: "/areas/yoga-trowbridge", desc: "County town, 15 min from Frome", distance: "15 min", featured: true },
    { name: "Warminster", link: "/areas/yoga-warminster", desc: "Garrison town on the A350", distance: "20 min" },
    { name: "Westbury", link: "/areas/yoga-westbury", desc: "White Horse town via A36", distance: "20 min" },
    { name: "Bradford-on-Avon", link: "/areas/yoga-bradford-on-avon", desc: "Historic wool town", distance: "20 min" },
    { name: "Melksham", link: "/yoga-melksham", desc: "Growing market town", distance: "25 min" },
    { name: "Devizes", link: "/yoga-devizes", desc: "Canal town, longer drive", distance: "30 min" },
  ];

  const services = [
    { name: "Hatha Yoga Trowbridge", link: "/areas/yoga-trowbridge/hatha-yoga", desc: "Traditional practice for Trowbridge residents" },
    { name: "Hatha Yoga Warminster", link: "/areas/yoga-warminster/hatha-yoga", desc: "Traditional practice for Warminster residents" },
    { name: "Yin Yoga Trowbridge", link: "/areas/yoga-trowbridge/yin-yoga", desc: "Deep stretches for Trowbridge residents" },
    { name: "Yin Yoga Westbury", link: "/areas/yoga-westbury/yin-yoga", desc: "Deep stretches for Westbury residents" },
    { name: "Private Sessions", link: "/yoga-classes/private-yoga", desc: "One-to-one yoga tailored to you" },
    { name: "Healing & Treatments", link: "/treatments", desc: "Massage, Reiki, energy healing" },
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={schema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Wiltshire" currentSlug="areas/yoga-wiltshire" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga for West Wiltshire
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Classes Wiltshire
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Small, personal yoga classes just across the Somerset border in Frome. 
              Perfect for <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link>, <Link to="/areas/yoga-warminster" className="text-gold hover:underline">Warminster</Link>, 
              and West Wiltshire residents looking for quality yoga without the drive to Bath or Salisbury.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Based in Frome (Somerset border)
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                15-25 min from West Wiltshire
              </span>
            </div>
            <Link to="/contact" className="btn-primary">
              Find Classes <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Wiltshire Coverage */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
            Yoga for West Wiltshire Residents
          </h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Frome sits right on the Somerset-Wiltshire border, making it an easy destination for 
              anyone in West Wiltshire looking for yoga. <Link to="/areas/yoga-trowbridge" className="text-gold hover:underline">Trowbridge</Link> is 
              just 15 minutes away, <Link to="/areas/yoga-warminster" className="text-gold hover:underline">Warminster</Link> and 
              <Link to="/areas/yoga-westbury" className="text-gold hover:underline"> Westbury</Link> about 20.
            </p>
            <p>
              Many of my Wiltshire students say the short drive becomes part of their practice — 
              a chance to leave work behind before they even arrive. And unlike larger town gyms, 
              my classes are capped at 6-8 people, so you actually get individual attention.
            </p>
            <p>
              I offer <Link to="/areas/yoga-trowbridge/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Trowbridge</Link> for 
              those wanting an active practice, <Link to="/areas/yoga-warminster/yin-yoga" className="text-gold hover:underline">Yin yoga near Warminster</Link> for 
              deep release, and <Link to="/yoga-classes/private-yoga" className="text-gold hover:underline">private sessions</Link> if 
              you prefer one-to-one attention. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Beginners</Link> are always welcome.
            </p>
          </div>
        </div>
      </section>

      {/* Wiltshire Locations Grid */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4 text-center">
            Yoga Classes for Wiltshire Towns
          </h2>
          <p className="text-neutral-600 text-center mb-10 max-w-2xl mx-auto">
            All classes held in Frome — click your town for directions and details.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wiltshireTowns.map((town, index) => (
              <motion.div
                key={town.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={town.link}
                  className={`block p-5 rounded-xl border transition-all group ${
                    town.featured 
                      ? 'bg-gold/10 border-gold/30 hover:border-gold' 
                      : 'bg-white/70 border-gold/10 hover:border-gold/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg text-neutral-800 group-hover:text-gold transition-colors">
                      {town.name}
                    </h3>
                    <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded-full">
                      {town.distance}
                    </span>
                  </div>
                  <p className="text-neutral-600 text-sm mb-2">{town.desc}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View details <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">
            Yoga Services for Wiltshire
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {services.map((service) => (
              <Link
                key={service.name}
                to={service.link}
                className="block bg-white/70 p-5 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group"
              >
                <h3 className="font-heading text-lg text-neutral-800 mb-2 group-hover:text-gold transition-colors">
                  {service.name}
                </h3>
                <p className="text-neutral-600 text-sm">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
            Start Yoga in Wiltshire
          </h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Get in touch and I'll help you find the right class for your schedule and experience level.
          </p>
          <Link to="/contact" className="btn-primary">
            Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
          </Link>
        </div>
      </section>

      {/* Somerset Link */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving Somerset:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-somerset" className="text-sm text-gold hover:underline font-medium">View Somerset locations →</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YogaWiltshire;
